import type { AskRedoPendingApproval } from "@redotech/merchant-sdk/rpc/schema/agents/ask-redo/get-ask-redo-threads";
import { isClientToolImageMessageText } from "@redotech/redo-agents-common/client-side-tool-call";
import type {
  AssistantMessageContent,
  FilePart,
  ImagePart,
  Message,
  ReasoningPart,
  TextPart,
  ToolCallPart,
  ToolResultOutput,
  ToolResultPart,
  UserMessageContent,
} from "@redotech/redo-agents-common/llm-messages";
import type { SeededEmail } from "@redotech/redo-agents-common/seeded-email";
import type { DynamicToolUIPart, UIMessagePart } from "ai";
import { withAskRedoConfirmationData } from "../confirmation-data";
import type { AskRedoUIMessage } from "../types";

/**
 * Convert persisted `LlmThread.messages` (Redo's internal shape) into
 * `UIMessage[]` that `useChat` can hydrate as `initialMessages`.
 *
 * Strategy:
 *   - `tool` messages (ToolResultPart[]) are matched against the
 *     preceding assistant's `tool-call` parts so we emit a single
 *     combined `tool-<name>` part with `state: "output-available"`.
 *   - An assistant tool-call with no result hydrates to
 *     `state: "approval-requested"` when it is in `pendingApprovals`
 *     (the interactive card survives reload); otherwise it is orphaned
 *     and renders terminal (`state: "output-error"`) instead of a
 *     perpetual spinner.
 *   - `system` messages are dropped (they never belong on the wire).
 *   - `string` content is normalized to a single text part.
 *
 * Known intentional drops:
 *   - `ReasoningPart.signature` is not preserved on the wire.
 *     Anthropic extended-thinking continuity is driven server-side
 *     from `LlmThread` (which keeps the signature); the client's
 *     UIMessage tree is display-only and does not feed back into
 *     model context. If we ever route `initialMessages` directly
 *     into a new agent call this assumption will need to be revisited.
 */
export function redoToUIMessages(
  messages: Message[],
  pendingApprovals: AskRedoPendingApproval[] = [],
  seededEmail?: SeededEmail,
): AskRedoUIMessage[] {
  const approvalsByToolCallId = new Map(
    pendingApprovals.map((approval) => [approval.toolCallId, approval]),
  );

  // First pass: collect tool results indexed by toolCallId so the
  // assistant-message pass can fold them in.
  const resultsByToolCallId = new Map<string, ToolResultPart>();
  for (const message of messages) {
    if (message.role !== "tool") {
      continue;
    }
    for (const part of message.content) {
      resultsByToolCallId.set(part.toolCallId, part);
    }
  }

  const uiMessages: AskRedoUIMessage[] = [];
  let messageIndex = 0;

  for (const message of messages) {
    if (message.role === "system" || message.role === "tool") {
      continue;
    }

    const id = message.id ?? `hydrated-${messageIndex++}`;

    if (message.role === "user") {
      // Server-injected screenshot carriers (client-tool image messages)
      // exist purely to put the captured image in model context — the
      // screenshot already renders in the tool's own card, so don't show
      // them as merchant-authored messages.
      if (isClientToolImageCarrier(message.content)) {
        continue;
      }
      uiMessages.push({
        id,
        role: "user",
        parts: convertUserContent(message.content),
        ...(seededEmail && seededEmail.messageId === message.id
          ? { metadata: { seededEmail } }
          : {}),
      });
      continue;
    }

    // assistant
    uiMessages.push({
      id,
      role: "assistant",
      parts: convertAssistantContent(
        message.content,
        resultsByToolCallId,
        approvalsByToolCallId,
      ),
    });
  }

  return uiMessages;
}

function isClientToolImageCarrier(
  content: string | UserMessageContent[],
): boolean {
  if (typeof content === "string") {
    return isClientToolImageMessageText(content);
  }
  const first = content[0];
  return (
    first?.type === "text" &&
    isClientToolImageMessageText((first as TextPart).text)
  );
}

function convertUserContent(
  content: string | UserMessageContent[],
): UIMessagePart<never, never>[] {
  if (typeof content === "string") {
    return [{ type: "text", text: content }];
  }
  const parts: UIMessagePart<never, never>[] = [];
  for (const part of content) {
    switch (part.type) {
      case "text":
        parts.push({ type: "text", text: (part as TextPart).text });
        break;
      case "image": {
        const image = part as ImagePart;
        parts.push({
          type: "file",
          mediaType: inferImageMediaType(image.image),
          url: image.image,
        });
        break;
      }
      case "file": {
        const file = part as FilePart;
        parts.push({
          type: "file",
          mediaType: file.mediaType,
          url: file.data,
          ...(file.filename ? { filename: file.filename } : {}),
        });
        break;
      }
    }
  }
  return parts;
}

function convertAssistantContent(
  content: string | AssistantMessageContent[],
  resultsByToolCallId: Map<string, ToolResultPart>,
  approvalsByToolCallId: Map<string, AskRedoPendingApproval>,
): UIMessagePart<never, never>[] {
  if (typeof content === "string") {
    return [{ type: "text", text: content }];
  }

  const parts: UIMessagePart<never, never>[] = [];

  for (const part of content) {
    switch (part.type) {
      case "text":
        parts.push({ type: "text", text: (part as TextPart).text });
        break;
      case "reasoning": {
        const reasoning = part as ReasoningPart;
        parts.push({ type: "reasoning", text: reasoning.text, state: "done" });
        break;
      }
      case "tool-call": {
        const toolCall = part as ToolCallPart;
        const result = resultsByToolCallId.get(toolCall.toolCallId);
        parts.push(
          buildToolPart(
            toolCall,
            result,
            approvalsByToolCallId.get(toolCall.toolCallId),
          ),
        );
        break;
      }
    }
  }

  return parts;
}

function buildToolPart(
  toolCall: ToolCallPart,
  result: ToolResultPart | undefined,
  approval: AskRedoPendingApproval | undefined,
): DynamicToolUIPart {
  // Ask Redo's tools are discovered at runtime from the agent
  // registry, so we don't supply a static `tools` map to useChat. The
  // AI SDK's `DynamicToolUIPart` (`type: "dynamic-tool"`) is the
  // canonical shape for that case — its discriminator is the runtime
  // `toolName` field, not a literal `tool-${name}` union member, so
  // it fits cleanly into `UIMessagePart` without any cast and matches
  // what the streaming path produces (the transport sets
  // `dynamic: true` on every tool chunk).
  const toolCallId = toolCall.toolCallId;
  const toolName = toolCall.toolName;
  const input = toolCall.input;

  if (!result) {
    // A parked approval hydrates to the durable Approve/Deny card (survives
    // reload); any other unmatched tool-call is orphaned and renders terminal.
    if (approval) {
      return {
        type: "dynamic-tool",
        toolName,
        toolCallId,
        state: "approval-requested",
        input: withAskRedoConfirmationData(
          input,
          approval.confirmationData,
          approval.prompt,
        ),
        approval: { id: toolCallId },
      };
    }
    return {
      type: "dynamic-tool",
      toolName,
      toolCallId,
      state: "output-error",
      input,
      errorText: "This action didn't finish.",
    };
  }

  if (result.isError || isErrorOutput(result.output)) {
    return {
      type: "dynamic-tool",
      toolName,
      toolCallId,
      state: "output-error",
      input,
      errorText: errorTextFor(result.output),
    };
  }

  return {
    type: "dynamic-tool",
    toolName,
    toolCallId,
    state: "output-available",
    input,
    output: extractValue(result.output),
  };
}

function isErrorOutput(output: ToolResultOutput): boolean {
  return output.type === "error-text" || output.type === "error-json";
}

function errorTextFor(output: ToolResultOutput): string {
  const value = output.value;
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function extractValue(output: ToolResultOutput): unknown {
  return output.value;
}

/**
 * Best-effort MIME type from an image URL. `ImagePart` in Redo only
 * carries the URL (no mime); AI SDK file parts want a concrete media
 * type so renderers can branch on `mediaType.startsWith("image/")`.
 * Falls back to `image/png` (a common default that always satisfies
 * the `image/*` prefix check) when the extension is unknown.
 */
function inferImageMediaType(url: string): string {
  const pathOnly = url.split("?")[0]?.split("#")[0] ?? url;
  const ext = pathOnly.split(".").pop()?.toLowerCase();
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (ext) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "svg":
      return "image/svg+xml";
    case "avif":
      return "image/avif";
    default:
      return "image/png";
  }
}
